import type { LeadFilter } from './api';

/**
 * React Query keys for the lead import workspace. Everything lives under the
 * 'lead-import' prefix; queryKeyRegistry maps each family to the replicated
 * tables it reads, so realtime changes refresh open views. Mutations still
 * invalidate the affected keys explicitly for the acting user.
 */
export const leadImportKeys = {
  all: ['lead-import'] as const,
  batches: (filter: Record<string, unknown> = {}) => ['lead-import', 'batches', filter] as const,
  batch: (id: number) => ['lead-import', 'batch', id] as const,
  summary: (id: number) => ['lead-import', 'batch', id, 'summary'] as const,
  duplicates: (id: number) => ['lead-import', 'batch', id, 'duplicates'] as const,
  exportPreview: (id: number, filter: unknown) => ['lead-import', 'batch', id, 'export', filter] as const,
  /** Mutation key of the sheet choice: the columns step waits while it saves. */
  sheetChoice: (id: number) => ['lead-import', 'batch', id, 'sheet-choice'] as const,
  verifyEstimate: (id: number, generation: number) => ['lead-import', 'batch', id, 'verify-estimate', generation] as const,
  previewRows: (id: number, generation: number, kind: string) => ['lead-import', 'batch', id, 'preview-rows', generation, kind] as const,
  /** Leads grids. Not under batch(id): one grid can span many imports. */
  leadsAll: ['lead-import', 'leads'] as const,
  leads: (filter: LeadFilter, sort: string, desc: boolean) => ['lead-import', 'leads', filter, sort, desc] as const,
  leadCount: (filter: LeadFilter) => ['lead-import', 'leads', 'count', filter] as const,
  lead: (id: number) => ['lead-import', 'lead', id] as const,
  merchantLeads: (merchant: number) => ['lead-import', 'merchant', merchant, 'leads'] as const,
  merchantFills: (merchant: number) => ['lead-import', 'merchant', merchant, 'fills'] as const,
  holds: (status: string | null, batch: number | null) => ['lead-import', 'holds', status, batch] as const,
  assignments: (batch: number | null) => ['lead-import', 'assignments', batch] as const,
  assignment: (id: number) => ['lead-import', 'assignment', id] as const,
  redistributionPreview: (request: unknown) => ['lead-import', 'redistribution', 'preview', request] as const,
  redistributionRuns: (rep: number | null, batch: number | null) => ['lead-import', 'redistribution', 'runs', rep, batch] as const,
  excluded: (request: unknown, kind: string) => ['lead-import', 'assignment-excluded', request, kind] as const,
  verificationRun: (id: number) => ['lead-import', 'verification-run', id] as const,
  purchases: ['lead-import', 'purchases'] as const,
  paymentCandidates: (purchase: number | null, search: string) => ['lead-import', 'payment-candidates', purchase, search] as const,
  profiles: ['lead-import', 'profiles'] as const,
  verificationAnswers: ['lead-import', 'verification-answers'] as const,
  report: (kind: string, from: string | null, to: string | null) => ['lead-import', 'report', kind, from, to] as const,
  sources: ['lead-import', 'ref', 'sources'] as const,
  reps: ['lead-import', 'ref', 'reps'] as const,
  openers: ['lead-import', 'ref', 'openers'] as const,
  eligible: (batch: number, rule: unknown) => ['lead-import', 'eligible', batch, rule] as const,
  targets: ['lead-import', 'ref', 'targets'] as const,
  neverFill: ['lead-import', 'ref', 'never-fill'] as const,
};
